import CommunicationsApi from '../communications/CommunicationsApi';
import type { CommunicationDetails } from '../communications/types';
import DocumentsApi from '../documents/DocumentsApi';
import type { ApiDocument } from '../documents/types';
import { ApiDocumentSource } from '../documents/types';
import type FetchService from '../FetchService';
import type { CombinePaginatedApiBaseParams, QueryFn } from '../util/api';
import { makeCombinedPaginatedQuery } from '../util/api';
import type {
  SharedApiCommonMetadata,
  SharedApiCommonQueryParams,
  SharedItemsCollection,
  SharedItemsReturnType,
} from './types';
import { sharedItemsCollection } from './types';

const EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_DAYS = 31;

class SharedApi {
  private documentsApi;
  private communicationsApi;

  constructor(private fetchService: FetchService) {
    this.documentsApi = new DocumentsApi(this.fetchService);
    this.communicationsApi = new CommunicationsApi(this.fetchService);
  }

  getExpiringDateISO = () => {
    const today = new Date();
    const expiringDate = new Date(today);
    expiringDate.setDate(expiringDate.getDate() + EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_DAYS);
    return expiringDate.toISOString();
  };

  getExpiringCommunications: QueryFn<CommunicationDetails, SharedItemsCollection> = async ({
    searchTerm,
    ...params
  }: CombinePaginatedApiBaseParams<SharedApiCommonQueryParams>) => {
    // GetCommunications doesn't supports filter parameters yet
    if ([params.labels, params.ownerId].some((param) => param != null)) {
      return {
        collection: sharedItemsCollection.EXPIRING_COMMUNICATION_DETAILS,
        items: [],
        total: 0,
        metadata: { numberUnseenExpiringCommunications: 0 },
      };
    }

    const res = await this.communicationsApi.getCommunications({
      ...params,
      beforeScheduledDeleteDate: this.getExpiringDateISO(),
      searchText: searchTerm,
      type: 'documents',
    });
    return {
      collection: sharedItemsCollection.EXPIRING_COMMUNICATION_DETAILS,
      items: res.communications ?? [],
      total: res.total ?? 0,
      metadata: { numberUnseenExpiringCommunications: res.numberUnseenCommunications },
    };
  };

  private getNotExpiringCommunications: QueryFn<CommunicationDetails, SharedItemsCollection> =
    async ({
      searchTerm,
      ...params
    }: CombinePaginatedApiBaseParams<SharedApiCommonQueryParams>) => {
      // GetCommunications doesn't supports filter parameters yet
      if ([params.labels, params.ownerId].some((param) => param != null)) {
        return {
          collection: sharedItemsCollection.NOT_EXPIRING_COMMUNICATION_DETAILS,
          items: [],
          total: 0,
          metadata: { numberUnseenCommunications: 0 },
        };
      }

      const res = await this.communicationsApi.getCommunications({
        ...params,
        afterScheduledDeleteDate: this.getExpiringDateISO(),
        searchText: searchTerm,
        type: 'documents',
      });
      return {
        collection: sharedItemsCollection.NOT_EXPIRING_COMMUNICATION_DETAILS,
        items: res.communications ?? [],
        total: res.total ?? 0,
        metadata: { numberUnseenCommunications: res.numberUnseenCommunications },
      };
    };

  private getPushedDocuments: QueryFn<ApiDocument, SharedItemsCollection, SharedApiCommonMetadata> =
    async ({
      searchTerm,
      ...params
    }: CombinePaginatedApiBaseParams<SharedApiCommonQueryParams>) => {
      const res = await this.documentsApi.getDocuments({
        sources: ApiDocumentSource.Study,
        states: 'PENDING',
        returnUniqueFilterValues: true,
        sort: 'creationDate,desc',
        documentName: searchTerm,
        ...params,
      });

      return {
        collection: sharedItemsCollection.PUSHED_DOCUMENTS,
        items: res.documents,
        total: res.total,
        metadata: {
          ...res.uniqueFilterValues,
          numberPendingPushedDocuments: res.total,
        },
      };
    };

  private getSharedDocuments: QueryFn<ApiDocument, SharedItemsCollection, SharedApiCommonMetadata> =
    async ({
      searchTerm,
      ...params
    }: CombinePaginatedApiBaseParams<SharedApiCommonQueryParams>) => {
      const res = await this.documentsApi.getDocuments({
        sources: ApiDocumentSource.Shared,
        states: ['ACTIVE', 'PENDING'],
        returnUniqueFilterValues: true,
        sort: 'creationDate,desc',
        documentName: searchTerm,
        ...params,
      });

      return {
        collection: sharedItemsCollection.SHARED_DOCUMENTS,
        items: res.documents,
        total: res.total,
        metadata: res.uniqueFilterValues,
      };
    };

  /**
   * Retrieves a combined collection of shared items including expiring communications, pushed documents,
   * non-expiring communications, and shared documents.
   *
   * @returns A combined paginated query function that returns:
   * - Expiring communications (those scheduled for deletion within 31 days)
   * - Pushed documents (study documents in PENDING state)
   * - Non-expiring communications
   * - Shared documents (documents with ACTIVE or PENDING state)
   *
   * The response includes metadata about unseen items and unique filter values where applicable.
   */
  getSharedItems = makeCombinedPaginatedQuery<
    SharedItemsReturnType,
    SharedItemsCollection,
    SharedApiCommonMetadata,
    SharedApiCommonQueryParams
  >()({
    queryFns: [
      this.getExpiringCommunications,
      this.getPushedDocuments,
      this.getNotExpiringCommunications,
      this.getSharedDocuments,
    ],
  });

  getLastSharedItems = async ({ limit }: { limit: number }) => {
    const [communications, sharedDocuments, pushedDocuments] = await Promise.all([
      this.getNotExpiringCommunications({ limit, offset: 0 }),
      this.getSharedDocuments({ limit, offset: 0 }),
      this.getPushedDocuments({ limit, offset: 0 }),
    ]);

    return [...communications.items, ...sharedDocuments.items, ...pushedDocuments.items]
      .sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime())
      .slice(0, limit);
  };
}

export default SharedApi;
